import React from 'react';
import {
    LEGAL_ENTITY,
    PRODUCT_NAME,
    CONTACT_EMAIL,
    WEBSITE_URL,
    GOVERNING_STATE,
} from './constants';
import { getLegalStyles } from './legalStyles';

function MailLink({ styles }) {
    return (
        <a href={`mailto:${CONTACT_EMAIL}`} className={styles.link}>
            {CONTACT_EMAIL}
        </a>
    );
}

/**
 * Full Terms of Service body. Used on /terms-of-service and in registration modals.
 * @param {{ variant?: 'page' | 'modal' }} props
 */
export function TermsOfServiceContent({ variant = 'page' }) {
    const s = getLegalStyles(variant);

    return (
        <div className="space-y-8 leading-relaxed">
            <section className={s.section}>
                <h2 className={s.h2}>1. Agreement to Terms</h2>
                <p className={s.p}>
                    These Terms of Service (“Terms”) govern access to and use of{' '}
                    <strong>{PRODUCT_NAME}</strong>, operated by <strong>{LEGAL_ENTITY}</strong>{' '}
                    (“we”, “us”, “our”), including {WEBSITE_URL}, the merchant dashboard, APIs, and
                    related services (the “Service”). By creating an account, clicking to accept, or
                    using the Service, you agree to these Terms and our{' '}
                    <a href="/privacy-policy" className={s.link}>
                        Privacy Policy
                    </a>
                    . If you do not agree, do not use the Service.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>2. The Service</h2>
                <p className={s.p}>
                    {PRODUCT_NAME} is a business-to-business loyalty platform. Merchants can create
                    digital loyalty programs, issue digital wallet passes, track
                    customer visits and rewards, manage staff with role-based access, run email (and,
                    on eligible plans, SMS) marketing campaigns, and view program analytics.
                </p>
                <p className={s.p}>
                    We may add, change, or discontinue features at any time. Beta or preview
                    features are provided “as is” and may be modified or withdrawn without notice.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>3. Eligibility</h2>
                <p className={s.p}>
                    The Service is intended for businesses and their authorized representatives. You
                    must be at least 18 years old and have authority to bind the business you
                    represent. You may not use the Service if you are barred under applicable law or
                    if a prior account was terminated for violation of these Terms.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>4. Accounts & Security</h2>
                <ul className={s.ul}>
                    <li className={s.li}>
                        You must provide accurate registration information and keep it current.
                    </li>
                    <li className={s.li}>
                        You are responsible for safeguarding login credentials and all activity under
                        your account (including staff users you invite).
                    </li>
                    <li className={s.li}>
                        Notify us promptly at <MailLink styles={s} /> if you suspect unauthorized
                        access.
                    </li>
                    <li className={s.li}>
                        Staff access is governed by roles you assign; you are responsible for
                        permissions granted to invitees.
                    </li>
                </ul>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>5. Merchant Responsibilities</h2>
                <p className={s.p}>As a Merchant using {PRODUCT_NAME}, you agree that you will:</p>
                <ul className={s.ul}>
                    <li className={s.li}>
                        Comply with all laws applicable to your business, loyalty program, and
                        communications (including privacy, marketing, consumer protection, and
                        anti-spam laws such as CAN-SPAM and TCPA).
                    </li>
                    <li className={s.li}>
                        Obtain and document any required consents before collecting customer contact
                        information or sending marketing email or SMS messages.
                    </li>
                    <li className={s.li}>
                        Provide your own privacy notice to end customers explaining how you use their
                        data, and honor access, correction, and deletion requests as required by law.
                    </li>
                    <li className={s.li}>
                        Only upload or process customer data you have the right to use, and do not
                        upload unlawful, infringing, or deceptive content.
                    </li>
                    <li className={s.li}>
                        Honor rewards, offers, and program rules you publish to customers.
                    </li>
                    <li className={s.li}>
                        Not use the Service to harass, discriminate, or send unsolicited bulk messages
                        to recipients who have not opted in.
                    </li>
                </ul>
                <p className={s.p}>
                    You are the merchant of record for your relationship with your customers.{' '}
                    {LEGAL_ENTITY} is not a party to transactions between you and your customers.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>6. Digital Wallet Passes</h2>
                <p className={s.p}>
                    Wallet passes are delivered to customers through supported mobile wallet apps on
                    their devices. We do not control device compatibility, pass display, notification
                    delivery, or outages on customer phones or wallet apps. Links on passes (such as
                    terms, privacy, and rewards pages) may point to {PRODUCT_NAME} or your configured
                    URLs.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>7. Messaging Features</h2>
                <p className={s.p}>
                    Email and SMS tools are provided for communications to your opted-in customers.
                    You are solely responsible for message content, frequency, and compliance.
                    Delivery networks may block messages that violate their rules. We may suspend
                    messaging if we detect abuse, high complaint rates, or legal risk.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>8. Subscriptions, Billing & Refunds</h2>
                <p className={s.p}>
                    Paid plans are billed in advance through our secure payment system. By subscribing,
                    you authorize recurring charges at the then-current price until you cancel. Prices
                    may change with reasonable notice. Taxes may apply based on your location.
                </p>
                <ul className={s.ul}>
                    <li className={s.li}>
                        Manage or cancel subscriptions through the billing portal in Settings.
                    </li>
                    <li className={s.li}>
                        Failure to pay may result in suspension or downgrade of features.
                    </li>
                    <li className={s.li}>
                        Refunds are governed by our{' '}
                        <a href="/refund-policy" className={s.link}>
                            Refund Policy
                        </a>
                        , except where a refund is required by law.
                    </li>
                </ul>
                <p className={s.p}>
                    Usage limits (passes, emails, SMS, rewards, etc.) depend on your subscription
                    tier and reset according to plan rules displayed in the dashboard.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>9. Acceptable Use</h2>
                <p className={s.p}>You may not, and may not permit others to:</p>
                <ul className={s.ul}>
                    <li className={s.li}>
                        Use the Service for illegal, fraudulent, or harmful purposes
                    </li>
                    <li className={s.li}>
                        Probe, scan, or test vulnerabilities without written authorization
                    </li>
                    <li className={s.li}>
                        Interfere with or overload the Service (including circumventing rate limits)
                    </li>
                    <li className={s.li}>
                        Reverse engineer or copy the Service except as permitted by law
                    </li>
                    <li className={s.li}>
                        Resell or white-label the Service without our written agreement
                    </li>
                    <li className={s.li}>
                        Misrepresent your identity or affiliation with any person or business
                    </li>
                </ul>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>10. Intellectual Property</h2>
                <p className={s.p}>
                    The Service, including software, branding, documentation, and design, is owned by{' '}
                    {LEGAL_ENTITY} or its licensors and protected by intellectual property laws. We
                    grant you a limited, non-exclusive, non-transferable license to use the Service
                    during your subscription for your internal business purposes.
                </p>
                <p className={s.p}>
                    You retain ownership of content you upload (such as logos and marketing copy). You
                    grant us a license to host, display, and process that content solely to operate
                    the Service. Feedback you provide may be used without restriction or compensation.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>11. No Business Guarantees</h2>
                <p className={s.p}>
                    {PRODUCT_NAME} does not guarantee revenue, customer growth, retention, redemption
                    rates, or marketing performance. Results depend on your business, offers, and
                    customer engagement.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>12. Disclaimers</h2>
                <p className={s.p}>
                    THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT
                    PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
                    MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO
                    NOT WARRANT UNINTERRUPTED, ERROR-FREE, OR SECURE OPERATION.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>13. Limitation of Liability</h2>
                <p className={s.p}>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, {LEGAL_ENTITY} AND ITS OFFICERS,
                    DIRECTORS, EMPLOYEES, AND SUPPLIERS WILL NOT BE LIABLE FOR ANY INDIRECT,
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
                    REVENUE, DATA, GOODWILL, OR BUSINESS OPPORTUNITIES, ARISING FROM OR RELATED TO
                    THE SERVICE.
                </p>
                <p className={s.p}>
                    OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF THESE TERMS OR THE SERVICE WILL
                    NOT EXCEED THE GREATER OF (A) AMOUNTS YOU PAID US IN THE THREE (3) MONTHS BEFORE
                    THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS ($100). Some
                    jurisdictions do not allow certain limitations; in those cases, our liability is
                    limited to the fullest extent permitted by law.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>14. Indemnification</h2>
                <p className={s.p}>
                    You will defend, indemnify, and hold harmless {LEGAL_ENTITY} from claims, damages,
                    losses, and expenses (including reasonable attorneys’ fees) arising from your use
                    of the Service, your customer data, your marketing messages, your violation of
                    these Terms or law, or dispute between you and your customers.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>15. Suspension & Termination</h2>
                <p className={s.p}>
                    We may suspend or terminate your access immediately if you breach these Terms,
                    fail to pay, pose a security or legal risk, or if required by law. You may cancel
                    your subscription at any time; access continues through
                    the end of the paid period unless otherwise stated in your plan.
                </p>
                <p className={s.p}>
                    Upon termination, your right to use the Service ends. Sections that by nature
                    should survive (including payment obligations, IP, disclaimers, liability limits,
                    indemnity, and governing law) will survive.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>16. Governing Law & Disputes</h2>
                <p className={s.p}>
                    These Terms are governed by the laws of the State of {GOVERNING_STATE}, United
                    States, without regard to conflict-of-law rules. Exclusive venue for disputes
                    arising from these Terms lies in the state or federal courts located in{' '}
                    {GOVERNING_STATE}, and you consent to personal jurisdiction there.
                </p>
                <p className={s.p}>
                    Before filing a claim, you agree to contact us at <MailLink styles={s} /> and
                    attempt to resolve the dispute informally for at least thirty (30) days.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>17. Changes to Terms</h2>
                <p className={s.p}>
                    We may modify these Terms by posting an updated version with a new effective date.
                    Material changes may be notified by email or in the Service. Continued use after
                    the effective date constitutes acceptance, except where prohibited by law.
                </p>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>18. General</h2>
                <ul className={s.ul}>
                    <li className={s.li}>
                        <strong>Entire agreement:</strong> These Terms, the Privacy Policy, and
                        Refund Policy are the entire agreement regarding the Service.
                    </li>
                    <li className={s.li}>
                        <strong>Severability:</strong> If any provision is unenforceable, the rest
                        remains in effect.
                    </li>
                    <li className={s.li}>
                        <strong>Assignment:</strong> You may not assign these Terms without our
                        consent; we may assign them in connection with a merger or sale.
                    </li>
                    <li className={s.li}>
                        <strong>Force majeure:</strong> We are not liable for delays caused by events
                        beyond our reasonable control.
                    </li>
                </ul>
            </section>

            <section className={s.section}>
                <h2 className={s.h2}>19. Contact</h2>
                <p className={s.p}>
                    <strong>{LEGAL_ENTITY}</strong>
                    <br />
                    Email: <MailLink styles={s} />
                    <br />
                    Website:{' '}
                    <a href={WEBSITE_URL} className={s.link} target="_blank" rel="noopener noreferrer">
                        {WEBSITE_URL}
                    </a>
                </p>
            </section>

            <p className={s.disclaimer}>
                These terms are provided for general informational purposes and do not constitute
                legal advice.
            </p>
        </div>
    );
}
